/**
 * Marine Reference API — boat year → make → model cascade.
 *
 * Backs the boat-entry step of the estimate flow, the way the automotive
 * reference endpoints back the car one. Reads the server's `boat_make` /
 * `boat_model` tables, populated from the vehDB feed.
 *
 * Two things about this API that are easy to get wrong:
 *
 * 1. Every response is enveloped as `{ success, data }` — callers here get the
 *    unwrapped array.
 * 2. Model names contain `/` and `(*)` — e.g. `H190B/BS(*)`. They MUST be
 *    URL-encoded or the un-encoded slash breaks the route. Never interpolate a
 *    model name straight into a path or query string.
 */

import { HTTPService } from './httpService';

const API = new HTTPService();

/**
 * The closed size-tier vocabulary on `boat_model.size_tier`. Null means
 * "not tiered". The server 400s (INVALID_SIZE_TIER) on anything else, so the
 * editor validates against this list before sending.
 */
export const SIZE_TIERS = ['S', 'M', 'L', 'XL'];

/** Unwrap the `{ success, data }` envelope; tolerate a bare array. */
const unwrap = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

/**
 * Surface the server's own message rather than a generic axios string —
 * these routes return useful 400s ("makeId or make is required") that the
 * caller should be able to show.
 */
const messageFrom = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/**
 * All boat makes, optionally filtered by substring. Year-agnostic — NOT part
 * of the year → make → model cascade the estimate flow uses. Kept for the
 * planned "Add new Make…" crowd-sourcing path, in the same spirit as
 * `isValidHin` in `src/util/marineService.js`.
 *
 * @param {string} [q] substring filter on make_name
 * @returns {Promise<Array>} [{ id, make_name, ... }]
 */
export const getBoatMakes = async (q, { silent = false } = {}) => {
  try {
    const params = {};
    if (q) params.q = q;
    const { data } = await API.get('/api/boats/makes', params, { silent });
    return unwrap(data);
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat makes.'));
  }
};

/**
 * Makes with at least one model generation covering `year` — the first step
 * of the cascade.
 *
 * @param {number|string} year
 * @returns {Promise<Array>} [{ make_id, make_name }]
 */
export const getBoatMakesForYear = async (year, { silent = false } = {}) => {
  if (year === undefined || year === null || year === '') {
    throw new Error('year is required');
  }
  try {
    const { data } = await API.get(
      `/api/boats/makes/${encodeURIComponent(year)}`,
      {},
      { silent }
    );
    return unwrap(data);
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat makes.'));
  }
};

/**
 * Model generations for a make covering `year`, carrying the FULL dimensions
 * and the vendor UUID — so choosing a model COMPLETES the cascade; there is
 * no third call.
 *
 * Exactly one of makeId / make is required; the server 400s otherwise.
 *
 * @param {{ makeId?: number|string, make?: string, year?: number|string, q?: string }} opts
 * @returns {Promise<Array>} [{ make_id, make_name, model_name, year_start, year_end,
 *   loa_ft, beam_ft, length_raw, beam_raw, boat_class, model_type, hull_code,
 *   engines, hp, weight_lb, fuel_type, hull_height_in, hull_height_source,
 *   size_tier ('S'|'M'|'L'|'XL'|null),
 *   boat_model_source, boat_model_uuid, source_url, source_confidence }]
 */
export const getBoatModels = async ({ makeId, make, year, q } = {}, { silent = false } = {}) => {
  if (makeId === undefined && !make) {
    throw new Error('makeId or make is required');
  }
  try {
    const params = {};
    if (makeId !== undefined && makeId !== null) params.makeId = makeId;
    else params.make = make;
    if (year !== undefined && year !== null && year !== '') params.year = year;
    if (q) params.q = q;
    const { data } = await API.get('/api/boats/models', params, { silent });
    return unwrap(data);
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat models.'));
  }
};

/**
 * Re-resolve a single boat model by its VENDOR uuid. NOT needed in the normal
 * cascade — getBoatModels already returns everything. Use for deep-linking or
 * re-resolving a saved boat. 404s on an unknown uuid.
 *
 * Additionally returns `hull_height_known` and a ready-made `hull_height_prompt`.
 */
export const getBoatModel = async (uuid, source = 'vehdb', { silent = false } = {}) => {
  if (!uuid) throw new Error('uuid is required');
  try {
    const { data } = await API.get(
      `/api/boats/model/${encodeURIComponent(uuid)}`,
      { source },
      { silent }
    );
    return data?.data ?? data ?? null;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load that boat model.'));
  }
};

// `buildMarineAttributes` and `isValidHin` deliberately live in
// `src/util/marineService.js` instead of here — they are pure, and that module
// has zero imports so the Node ESM harness can test them directly. This module
// imports HTTPService and cannot be harness-tested.

// ---------------------------------------------------------------------------
// Catalog admin — the Water half of /vehicles
// ---------------------------------------------------------------------------

/**
 * Paginated boat catalog, grouped by make + model.
 *
 * Groups, not rows: every generation of a make+model arrives together, so a
 * group can never straddle a page boundary and render half its class rows.
 * `total` is the GROUP count.
 */
export const getBoatCatalog = async ({ page = 1, pageSize = 50, q, make, includeInactive } = {}) => {
  try {
    const params = { page, pageSize };
    if (q) params.q = q;
    if (make) params.make = make;
    if (includeInactive) params.includeInactive = 1;
    const { data } = await API.get('/api/boats/catalog', params);
    const payload = data?.data ?? data ?? {};
    return {
      groups: Array.isArray(payload.groups) ? payload.groups : [],
      total: Number(payload.total) || 0,
      page: Number(payload.page) || page,
      pageSize: Number(payload.pageSize) || pageSize,
    };
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load the boat catalog.'));
  }
};

/** The class vocabulary. Feed the result straight to `parseClasses`. */
export const getBoatClasses = async () => {
  try {
    const { data } = await API.get('/api/boats/classes');
    return data?.data ? data : { data: unwrap(data) };
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not load boat classes.'));
  }
};

/**
 * Update one generation. Partial — send only what changed.
 *
 * An explicit `null` CLEARS a dimension; the server rejects 0 as "not a positive
 * number". Never coerce a blank field to 0 on the way in.
 *
 * `size_tier` is accepted here too: one of `SIZE_TIERS`, or `null` to clear.
 * Any other value is a 400.
 */
export const updateBoatModel = async (id, changes) => {
  if (!id) throw new Error('id is required');
  try {
    const { data } = await API.put(`/api/boats/model/${encodeURIComponent(id)}`, changes);
    return data?.data ?? data;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not save that craft.'));
  }
};

/** Atomic class reassignment across N generations. Rejects the whole batch on an unknown slug. */
export const bulkSetBoatClass = async (ids, boatClass) => {
  if (!Array.isArray(ids) || ids.length === 0) throw new Error('ids is required');
  if (!boatClass) throw new Error('boat_class is required');
  try {
    const { data } = await API.put('/api/boats/models/bulk-class', { ids, boat_class: boatClass });
    return data?.data ?? data;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not apply that class.'));
  }
};

/**
 * Atomic size-tier reassignment across N generations. Rejects the whole batch
 * on a value outside `SIZE_TIERS` (400 INVALID_SIZE_TIER).
 *
 * ASSUMED PATH: `PUT /api/boats/models/bulk-size-tier`, mirroring the
 * bulk-class setter above with the same `{ ids, size_tier }` body shape. The
 * server contract names the body but not the route — confirm before relying
 * on this in production. The per-id `updateBoatModel(id, { size_tier })`
 * already works, so this is an optimisation, not a prerequisite.
 */
export const bulkSetSizeTier = async (ids, sizeTier) => {
  if (!Array.isArray(ids) || ids.length === 0) throw new Error('ids is required');
  // null CLEARS the tier on every id (the craft gets no By-size rows) — confirmed
  // by the server 2026-09-09; anything else outside the vocabulary is refused.
  if (sizeTier !== null && !SIZE_TIERS.includes(sizeTier)) throw new Error('size_tier must be one of S, M, L, XL, or null to clear');
  try {
    const { data } = await API.put('/api/boats/models/bulk-size-tier', { ids, size_tier: sizeTier });
    return data?.data ?? data;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not apply that size tier.'));
  }
};

/** Hand-entered make. 409s on a duplicate name. */
export const createBoatMake = async (makeName) => {
  if (!makeName) throw new Error('make_name is required');
  try {
    const { data } = await API.post('/api/boats/makes', { make_name: makeName });
    return data?.data ?? data;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not add that make.'));
  }
};

/** Hand-entered craft. 409s against the natural key (make_id, model_name, year_start). */
export const createBoatModel = async (payload) => {
  try {
    const { data } = await API.post('/api/boats/models', payload);
    return data?.data ?? data;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not add that craft.'));
  }
};

/**
 * Deactivate (or reactivate) a generation. NEVER a hard delete.
 *
 * A saved boat links to `boat_model` by string, with no FK, so a hard delete
 * would not error — it would silently drop the model tier out of freeboard
 * resolution and requote the customer's boat at a different price. The server
 * 409s with an attached-boat count when a customer is booked against the row;
 * surface that count rather than a generic failure.
 */
export const setBoatModelActive = async (id, active) => {
  if (!id) throw new Error('id is required');
  try {
    const { data } = await API.put(`/api/boats/model/${encodeURIComponent(id)}/active`, { active: active ? 1 : 0 });
    return data?.data ?? data;
  } catch (error) {
    throw new Error(messageFrom(error, 'Could not change that craft\'s status.'));
  }
};

const marineReferenceService = {
  getBoatMakes,
  getBoatMakesForYear,
  getBoatModels,
  getBoatModel,
  getBoatCatalog,
  getBoatClasses,
  updateBoatModel,
  bulkSetBoatClass,
  bulkSetSizeTier,
  createBoatMake,
  createBoatModel,
  setBoatModelActive,
};

export default marineReferenceService;
